import React from 'react'
import { Text, View } from 'react-native'
import Animated, { cancelAnimation, Easing, useAnimatedStyle, useReducedMotion, useSharedValue, withRepeat, withSequence, withTiming } from 'react-native-reanimated'
import { CardHudButton, CardHudFace } from '../components/cards/CardHudButton'
import { useTheme } from '../hooks/useTheme'
import type { CardSetConcept } from './cardSetConcepts'

export type SampleHand = 'none' | 'ready'
export const SAMPLE_HAND_COUNTS: Record<SampleHand, number> = { none: 2, ready: 4 }

/** QA accents around the exact same card face and count as the live game. */
export function CardSetConceptButton({ concept, hand, animate, onPress, compactSize }: {
  concept: CardSetConcept
  hand: SampleHand
  animate: boolean
  onPress: () => void
  compactSize?: 26 | 30
}) {
  const { theme } = useTheme()
  const reducedMotion = useReducedMotion()
  const dark = theme === 'dark'
  const active = hand !== 'none'
  const paper = dark ? '#2d351f' : '#f4ecd3'
  const olive = dark ? '#a6bc65' : '#607035'
  const accent = concept.treatment === 'brass' ? (dark ? '#c5a360' : '#967238') : olive
  const neutral = dark ? '#7d914c' : '#718349'
  const progress = useSharedValue(1)
  const motion = animate && !reducedMotion && active ? concept.motion : 'none'
  React.useEffect(() => {
    cancelAnimation(progress)
    if (motion === 'none') progress.value = 1
    else if (motion === 'arrival') {
      progress.value = 0
      progress.value = withTiming(1, { duration: 650, easing: Easing.out(Easing.quad) })
    } else if (motion === 'lift') {
      progress.value = 1
      progress.value = withSequence(withTiming(0, { duration: 450 }), withTiming(1, { duration: 750 }))
    } else {
      progress.value = 1
      const duration = motion === 'border' ? 3500 : 4000
      progress.value = withRepeat(withTiming(0, { duration, easing: Easing.inOut(Easing.quad) }), -1, true)
    }
    return () => cancelAnimation(progress)
  }, [motion, progress])
  const accentStyle = useAnimatedStyle(() => ({ opacity: motion === 'arrival' ? progress.value : 0.4 + 0.35 * progress.value }))
  const liftStyle = useAnimatedStyle(() => ({ transform: [{ translateY: motion === 'lift' ? progress.value - 1 : 0 }] }))
  const marker = concept.treatment === 'check' || concept.treatment === 'outline' || concept.treatment === 'wash' || concept.treatment === 'brass' || concept.treatment === 'badge' || concept.treatment === 'inset-check'
  const insetCheck = concept.treatment === 'inset-check'
  const edge = active && concept.treatment !== 'check' ? accent : neutral
  const wash = active && concept.treatment === 'wash' ? (dark ? '#3d4829' : '#e2e6c9') : paper
  if (concept.id === 'soft-halo') return <CardHudButton handCount={SAMPLE_HAND_COUNTS[hand]}
    canTurnIn={active} mustTurnIn={false} onPress={onPress} compact={compactSize !== undefined} compactSize={compactSize} />
  return <Animated.View style={liftStyle}>
    <CardHudFace handCount={SAMPLE_HAND_COUNTS[hand]} canTurnIn={active} mustTurnIn={false}
      onPress={onPress} compact={compactSize !== undefined} compactSize={compactSize}
      badgeColor={active && concept.treatment === 'badge' ? '#607035' : undefined}
      style={{ backgroundColor: wash, borderColor: edge, borderWidth: active && concept.treatment === 'double' ? 2 : 1 }}>
      {active && marker && motion !== 'dot' ? <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: insetCheck ? 1 : -3, bottom: insetCheck ? 1 : -3, width: 12, height: 12, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: insetCheck ? 'transparent' : accent, borderWidth: insetCheck ? 0 : 1, borderColor: paper }, motion === 'arrival' ? accentStyle : undefined]}>
        <Text accessible={false} style={{ color: insetCheck ? accent : dark ? '#18170f' : '#fff', fontWeight: '800', fontSize: 9, lineHeight: 10 }}>✓</Text>
      </Animated.View> : null}
      {active && concept.treatment === 'double' ? <View pointerEvents="none" style={{ position: 'absolute', inset: 3, borderWidth: 1, borderColor: accent, opacity: 0.45, borderRadius: 3 }} /> : null}
      {active && concept.treatment === 'pips' ? <View pointerEvents="none" style={{ position: 'absolute', bottom: 2, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center', gap: 2 }}>
        {[0, 1, 2].map(i => <View key={i} style={{ width: 3, height: 2, borderRadius: 1, backgroundColor: accent }} />)}
      </View> : null}
      {active && concept.treatment === 'tab' ? <View pointerEvents="none" style={{ position: 'absolute', left: 4, right: 4, bottom: -5, backgroundColor: accent, borderRadius: 2, alignItems: 'center' }}>
        <Text accessible={false} className="font-stencil" style={{ color: dark ? '#18170f' : '#fff', fontSize: 6, lineHeight: 9 }}>SET</Text>
      </View> : null}
      {active && motion === 'border' ? <Animated.View pointerEvents="none" style={[{ position: 'absolute', inset: -2, borderRadius: 7, borderWidth: 1, borderColor: accent }, accentStyle]} /> : null}
      {active && motion === 'underline' ? <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 6, right: 6, bottom: -2, height: 2, borderRadius: 1, backgroundColor: accent }, accentStyle]} /> : null}
      {active && motion === 'badge' ? <Animated.View pointerEvents="none" style={[{ position: 'absolute', top: -9, right: -9, width: 21, height: 21, borderRadius: 11, borderWidth: 1, borderColor: accent }, accentStyle]} /> : null}
      {active && motion === 'dot' ? <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 2, bottom: 2, width: 4, height: 4, borderRadius: 2, backgroundColor: accent }, accentStyle]} /> : null}
    </CardHudFace>
  </Animated.View>
}
